/** Ajustes: o que NÃO move número — faixas de saúde, categorias, família, tema, sessão.
 *
 * O diário e o colchão moravam aqui e foram para o Planejado quando o Paulo perguntou por
 * que uma decisão de dinheiro estava em configurações. A régua é essa: se muda a projeção,
 * é planejamento; se muda só como se lê, é ajuste. */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  Empty,
  ErrorBanner,
  Face,
  Field,
  FieldGroup,
  Key,
  Label,
  Skeleton,
  zoneClass,
  zoneLabel,
} from "../components/ui";
import { useToast } from "../components/Toast";
import { hojeLocal, money, parseMoney } from "../lib/format";
import { usePrefs, type Theme } from "../lib/prefs";
import {
  useAccounts,
  useCreateAccount,
  useCreatePlan,
  useSettingsQuery,
  useUpdateSettings,
} from "../lib/queries";
import { useSession } from "../lib/session";
import * as api from "../lib/api";
import type { Account, AccountKind, HealthLevel, ZoneColor } from "../lib/types";

/** As mesmas cores do editor de conta: a paleta é fechada porque cor aqui é identidade,
 *  não decoração, e uma escolha livre acabaria colidindo com as zonas de saúde. */
const CORES_CONTA = [
  "#2F6F4E",
  "#10713F",
  "#B02626",
  "#A66A07",
  "#24519B",
  "#5B3E96",
  "#0F7A86",
  "#8A5A2B",
  "#394145",
];

const KINDS: { id: AccountKind; label: string }[] = [
  { id: "checking", label: "Conta corrente" },
  { id: "cash", label: "Dinheiro" },
  { id: "savings", label: "Reserva" },
  { id: "investment", label: "Investimento" },
  { id: "credit_card", label: "Cartão de crédito" },
  { id: "loan", label: "Crediário" },
];

/** A tecla do sistema mede 32px; no dedo o alvo mínimo é 44. */
const ALVO = { minHeight: 44 };

const num = (s: string) => parseMoney(s);

export function Ajustes() {
  const nav = useNavigate();
  const { user, sair } = useSession();
  const { theme, setTheme } = usePrefs();
  const { data, isPending } = useSettingsQuery();

  if (isPending) return <section className="view on"><Skeleton lines={5} /></section>;

  return (
    <section className="view on" aria-label="Ajustes">
      <div className="plan">
        <div className="monthbar">
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <Key icon="chevronLeft" title="Voltar" onClick={() => nav(-1)} />
            <h2>Ajustes</h2>
          </div>
        </div>

        <Face className="log">
          <div className="sec">
            <Label>Mais ajustes</Label>
          </div>
          <div className="line">
            <div className="d" />
            <div className="what">
              <div className="t">
                <span className="desc">Família</span>
              </div>
              <small>Ver junto com quem divide as contas: criar, adicionar e compartilhar.</small>
            </div>
            <div style={{ paddingRight: 14 }}>
              <Key icon="users" style={ALVO} title="Abrir Família" onClick={() => nav("/ajustes/familia")}>
                Abrir
              </Key>
            </div>
          </div>
          <div className="line">
            <div className="d" />
            <div className="what">
              <div className="t">
                <span className="desc">Categorias</span>
              </div>
              <small>Criar, renomear e excluir; restaurar as padrão.</small>
            </div>
            <div style={{ paddingRight: 14 }}>
              <Key icon="tags" style={ALVO} title="Abrir Categorias" onClick={() => nav("/ajustes/categorias")}>
                Abrir
              </Key>
            </div>
          </div>
        </Face>

        <FaixasDeSaude faixas={data?.settings.health_levels ?? SEM_FAIXAS} />

        <Face className="meter">
          <Label>Tema</Label>
          <div className="seg" role="group" aria-label="Tema" style={{ marginTop: 6 }}>
            {(["light", "dark", "system"] as Theme[]).map((t) => (
              <button key={t} type="button" aria-pressed={theme === t} onClick={() => setTheme(t)}>
                {t === "light" ? "Claro" : t === "dark" ? "Escuro" : "Sistema"}
              </button>
            ))}
          </div>
        </Face>

        <Face className="meter">
          <Label>Sessão</Label>
          <div className="sub">
            {user?.name} · {user?.email}
          </div>
          <div style={{ marginTop: 8 }}>
            <Key onClick={() => sair().then(() => nav("/"))}>Sair</Key>
          </div>
        </Face>

        <TrocarSenha />
      </div>

    </section>
  );
}

// Identidade estável: `?? []` a cada render faria o efeito de sincronia do editor girar sem fim.
const SEM_FAIXAS: HealthLevel[] = [];

/** As cinco zonas do sistema (IEC 60073). A cor da faixa é significado — vermelho é "pare",
 *  verde é "siga" —, por isso o editor oferece as zonas e não um seletor de cor livre. */
const ZONAS: ZoneColor[] = ["rose", "orange", "amber", "emerald", "indigo"];

type Linha = { id: string; label: string; min: string; max: string; color: ZoneColor };

const paraLinha = (f: HealthLevel): Linha => ({
  id: f.id,
  label: f.label,
  min: f.min == null ? "" : String(f.min).replace(".", ","),
  max: f.max == null ? "" : String(f.max).replace(".", ","),
  color: f.color,
});

/** Campo vazio = sem limite (−∞ ou +∞ para o motor). */
const limite = (s: string): number | null => (s.trim() === "" ? null : num(s));

/** Ordena pelo mínimo tratando o vazio como −∞, sem cair no NaN de (−∞) − (−∞). */
function porMinimo(a: { min: number | null }, b: { min: number | null }): number {
  if (a.min === b.min) return 0;
  if (a.min === null) return -1;
  if (b.min === null) return 1;
  return a.min - b.min;
}

function emPalavras(min: number | null, max: number | null): string {
  if (min === null && max === null) return "qualquer saldo";
  if (min === null) return `abaixo de ${money(max as number)}`;
  if (max === null) return `${money(min)} ou mais`;
  return `de ${money(min)} a ${money(max)}`;
}

/** O motor casa `mínimo ≤ saldo < máximo` e devolve cinza quando nenhuma faixa responde —
 *  um buraco na régua pintaria o painel de "atenção" num dia de desastre. Daí a checagem. */
function problema(ls: Linha[]): string | null {
  if (ls.some((l) => !l.label.trim())) return "Toda faixa precisa de um nome.";
  const fs = ls.map((l) => ({ label: l.label.trim(), min: limite(l.min), max: limite(l.max) }));
  for (const f of fs)
    if (f.min !== null && f.max !== null && f.min >= f.max)
      return `Em “${f.label}”, o mínimo tem de ser menor que o máximo.`;
  const ord = [...fs].sort(porMinimo);
  const primeira = ord[0];
  const ultima = ord[ord.length - 1];
  if (primeira.min !== null)
    return `Abaixo de ${money(primeira.min)} nenhuma faixa responde: deixe o mínimo de “${primeira.label}” vazio.`;
  if (ultima.max !== null)
    return `Acima de ${money(ultima.max)} nenhuma faixa responde: deixe o máximo de “${ultima.label}” vazio.`;
  for (let i = 0; i < ord.length - 1; i++)
    if (ord[i].max !== ord[i + 1].min)
      return `Entre “${ord[i].label}” e “${ord[i + 1].label}” a régua tem furo ou sobreposição: o máximo de uma é o mínimo da seguinte.`;
  return null;
}

/** Faixas de saúde: a cor que o painel usa para dizer como está o saldo de cada dia.
 *  A API aceitava `health_levels` desde a F1 e nenhuma tela mandava (lacuna L17). */
function FaixasDeSaude({ faixas }: { faixas: HealthLevel[] }) {
  const salvar = useUpdateSettings();
  const [aberto, setAberto] = useState(false);
  const [linhas, setLinhas] = useState<Linha[]>(() => faixas.map(paraLinha));

  // As faixas chegam depois (a consulta resolve): repõe o rascunho só com o editor fechado,
  // para não atropelar o que a pessoa está digitando.
  useEffect(() => {
    if (!aberto) setLinhas(faixas.map(paraLinha));
  }, [faixas, aberto]);

  const previa = useMemo(() => [...faixas].sort(porMinimo), [faixas]);
  const erroDeRegua = useMemo(() => (linhas.length > 0 ? problema(linhas) : null), [linhas]);

  const trocar = (i: number, campo: Partial<Linha>) =>
    setLinhas((atual) => atual.map((l, j) => (j === i ? { ...l, ...campo } : l)));

  const enviar = () => {
    const health_levels: HealthLevel[] = [...linhas]
      .map((l) => ({
        id: l.id,
        label: l.label.trim(),
        min: limite(l.min),
        max: limite(l.max),
        color: l.color,
      }))
      .sort(porMinimo);
    salvar.mutate({ health_levels }, { onSuccess: () => setAberto(false) });
  };

  return (
    <Face className="meter">
      <Label>Faixas de saúde</Label>
      <div className="sub">
        A cor de cada dia na tela Fluxo e da face do painel em Hoje. O saldo do dia cai numa
        faixa e a faixa dá a cor.
      </div>

      {previa.length === 0 ? (
        <Empty title="Nenhuma faixa" />
      ) : (
        <>
          <div className="alloc" aria-hidden="true" style={{ marginTop: 6 }}>
            {previa.map((f) => (
              <i key={f.id} style={{ flex: 1, background: `var(--z-${zoneClass(f.color)})` }} />
            ))}
          </div>
          <div style={{ display: "grid", gap: 4, marginTop: 6 }}>
            {previa.map((f) => (
              <div
                key={f.id}
                style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}
              >
                <span className={`zone ${zoneClass(f.color)}`}>{f.label}</span>
                <span className="rule">{emPalavras(f.min, f.max)}</span>
              </div>
            ))}
          </div>
          <div className="rule" style={{ marginTop: 4 }}>
            A régua mostra a ordem e a cor, não a escala. O mínimo entra na faixa; o máximo já é
            a faixa seguinte.
          </div>
        </>
      )}

      {!aberto && previa.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <Key style={ALVO} onClick={() => setAberto(true)}>
            Editar faixas
          </Key>
        </div>
      )}

      {aberto &&
        linhas.map((l, i) => (
          <div
            key={l.id}
            style={{ display: "grid", gap: 6, marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--grid)" }}
          >
            <Field label="Nome da faixa">
              <input value={l.label} onChange={(e) => trocar(i, { label: e.target.value })} />
            </Field>
            <FieldGroup label="De / até (vazio = sem limite)">
              <div className="row2">
                <input
                  inputMode="decimal"
                  value={l.min}
                  aria-label={`Mínimo de ${l.label}`}
                  placeholder="sem limite"
                  onChange={(e) => trocar(i, { min: e.target.value })}
                />
                <input
                  inputMode="decimal"
                  value={l.max}
                  aria-label={`Máximo de ${l.label}`}
                  placeholder="sem limite"
                  onChange={(e) => trocar(i, { max: e.target.value })}
                />
              </div>
            </FieldGroup>
            <FieldGroup label="Cor">
              <div className="chips">
                {ZONAS.map((z) => (
                  <button
                    key={z}
                    type="button"
                    className="chip"
                    style={ALVO}
                    aria-pressed={l.color === z}
                    onClick={() => trocar(i, { color: z })}
                  >
                    <i style={{ background: `var(--z-${zoneClass(z)})` }} />
                    {zoneLabel(z)}
                  </button>
                ))}
              </div>
            </FieldGroup>
          </div>
        ))}

      {aberto && (
        <>
          {erroDeRegua && <div className="rule">{erroDeRegua}</div>}
          {salvar.isError && <ErrorBanner message={(salvar.error as Error).message} />}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <Key style={ALVO} onClick={() => setAberto(false)}>
              Cancelar
            </Key>
            <Key primary style={ALVO} disabled={!!erroDeRegua || salvar.isPending} onClick={enviar}>
              {salvar.isPending ? "Salvando…" : "Salvar faixas"}
            </Key>
          </div>
        </>
      )}
    </Face>
  );
}

const MINIMO = 10;

/** Trocar a senha sem depender de ninguém com acesso ao servidor. */
function TrocarSenha() {
  const [aberto, setAberto] = useState(false);
  const [atual, setAtual] = useState("");
  const [nova, setNova] = useState("");
  const [repete, setRepete] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [pronto, setPronto] = useState(false);

  const curta = nova.length > 0 && nova.length < MINIMO;
  const difere = repete.length > 0 && nova !== repete;
  const podeSalvar = atual.length > 0 && nova.length >= MINIMO && nova === repete && !salvando;

  function fechar() {
    setAberto(false);
    setAtual("");
    setNova("");
    setRepete("");
    setErro(null);
  }

  async function enviar() {
    setSalvando(true);
    setErro(null);
    try {
      await api.changePassword(atual, nova);
      setPronto(true);
      fechar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  if (!aberto) {
    return (
      <Face className="meter">
        <Label>Senha</Label>
        <div className="sub">
          {pronto
            ? "Senha trocada. As outras sessões foram desconectadas."
            : "Trocar a senha desconecta os outros aparelhos. Este continua conectado."}
        </div>
        <div style={{ marginTop: 8 }}>
          <Key onClick={() => { setPronto(false); setAberto(true); }}>Trocar senha</Key>
        </div>
      </Face>
    );
  }

  return (
    <Face className="meter">
      <Label>Trocar senha</Label>
      <Field label="Senha atual">
        <input
          type="password"
          autoComplete="current-password"
          value={atual}
          onChange={(e) => setAtual(e.target.value)}
        />
      </Field>
      <Field label="Senha nova">
        <input
          type="password"
          autoComplete="new-password"
          value={nova}
          onChange={(e) => setNova(e.target.value)}
        />
      </Field>
      <div className="sub">
        {curta ? `Faltam ${MINIMO - nova.length} caracteres.` : `No mínimo ${MINIMO} caracteres.`}
      </div>
      <Field label="Repita a senha nova">
        <input
          type="password"
          autoComplete="new-password"
          value={repete}
          onChange={(e) => setRepete(e.target.value)}
        />
      </Field>
      {difere && <div className="sub">As duas não são iguais.</div>}
      {erro && <ErrorBanner message={erro} />}
      <div className="acts">
        <Key onClick={fechar}>Cancelar</Key>
        <Key primary disabled={!podeSalvar} onClick={enviar}>
          {salvando ? "Trocando…" : "Trocar senha"}
        </Key>
      </div>
    </Face>
  );
}

export function NovaConta({ onClose, inicial }: { onClose: () => void; inicial?: AccountKind }) {
  const criar = useCreateAccount();
  const criarPlano = useCreatePlan();
  const aviso = useToast();
  const { data: contas } = useAccounts();
  const [nome, setNome] = useState("");
  const [kind, setKind] = useState<AccountKind>(inicial ?? "checking");
  const [saldo, setSaldo] = useState("0");
  const [fecha, setFecha] = useState(10);
  const [vence, setVence] = useState(20);
  const [limite, setLimite] = useState("");
  const [liquidaEm, setLiquidaEm] = useState("");
  // A cor é identidade da conta, e é por ela que se reconhece a linha no fluxo. Pedir na
  // criação evita que toda conta nasça cinza e só ganhe cara se alguém for editar depois.
  const [cor, setCor] = useState(CORES_CONTA[0]);
  const [erro, setErro] = useState<string | null>(null);
  // A dívida do crediário. Opcional: quem só quer a conta deixa em branco e cria do mesmo
  // jeito. Quem já sabe o que deve declara aqui e sai com a dívida na projeção, em vez de
  // criar uma conta de passivo vazia e ter de achar o "Novo parcelamento" em outra tela.
  const [credor, setCredor] = useState("");
  const [porTotal, setPorTotal] = useState(true);
  const [valorDivida, setValorDivida] = useState("");
  const [vezes, setVezes] = useState("");
  const [primeira, setPrimeira] = useState(hojeLocal());
  const [jaPagas, setJaPagas] = useState("0");

  const cartao = kind === "credit_card";
  const crediario = kind === "loan";
  const passivo = cartao || crediario;
  // De onde sai o dinheiro para pagar a fatura. Sem isso o motor avisa que a fatura do
  // cartão não entra na projeção — o cartão nasceria decorativo.
  const gastaveis = (contas ?? []).filter((a) => a.in_cashflow);
  const liquidacao = liquidaEm || gastaveis[0]?.id || "";
  const contaLiquidacao = gastaveis.find((a) => a.id === liquidacao);

  const parcelas = Math.max(0, Math.floor(Number(vezes) || 0));
  const informado = parseMoney(valorDivida);
  // A API guarda o TOTAL. Quem tem na mão o valor da parcela (o que a loja diz) multiplica
  // aqui — é a mesma conta que a tela "Novo parcelamento" faz.
  const total = porTotal ? informado : Math.round(informado * parcelas * 100) / 100;
  const pagas = Math.min(parcelas, Math.max(0, Math.floor(Number(jaPagas) || 0)));
  // Só vira plano com valor E número de parcelas: meia dívida não dá para projetar.
  const declaraDivida = crediario && total > 0 && parcelas >= 1;
  const ocupado = criar.isPending || criarPlano.isPending;

  const enviar = async () => {
    setErro(null);
    let conta: Account;
    try {
      conta = await criar.mutateAsync({
        name: nome,
        kind,
        color: cor,
        initial_balance: passivo ? 0 : parseMoney(saldo),
        ...(cartao ? { closing_day: fecha, due_day: vence } : {}),
        ...(cartao && limite ? { credit_limit: parseMoney(limite) } : {}),
        ...(passivo && liquidacao ? { settlement_account_id: liquidacao } : {}),
        // Só o credor vai para a conta. `installment_total` e `installment_amount` também
        // existem em `AccountIn`, mas ninguém os lê e eles envelheceriam parados enquanto o
        // plano — a fonte de verdade da dívida — muda a cada parcela paga. Um número, uma fonte.
        ...(crediario && credor.trim() ? { payee: credor.trim() } : {}),
      });
    } catch (e) {
      setErro((e as Error).message);
      return;
    }

    if (!declaraDivida) {
      // O aviso é só do crediário: é o único que sai daqui podendo não ter dívida nenhuma, e
      // saber disso importa (sem parcela ele não move dinheiro). Nas outras contas a placa
      // aparecendo na lista já é a prova de que deu certo.
      if (crediario) aviso.ok(`${conta.name} criado — sem dívida declarada.`);
      onClose();
      return;
    }

    try {
      await criarPlano.mutateAsync({
        description: credor.trim() || conta.name,
        account_id: conta.id,
        total_amount: total,
        installment_count: parcelas,
        first_due_on: primeira,
        already_paid: pagas,
        payee: credor.trim() || null,
      });
      aviso.ok(`${conta.name}: ${parcelas}× · total ${money(total)} na projeção.`);
    } catch (e) {
      // A conta já foi criada. Fechar em silêncio deixaria um crediário sem a dívida que a
      // pessoa acabou de digitar e sem nada dizendo por quê — o aviso de erro fica na tela
      // até ser dispensado, e diz o motivo que o servidor deu.
      aviso.erro(
        `Crediário ${conta.name} criado, mas o parcelamento não entrou: ${(e as Error).message} — declare a dívida em Planejado → Novo parcelamento.`,
      );
    }
    onClose();
  };

  return (
    <div className="sheet on" role="dialog" aria-modal="true" aria-label="Nova conta">
      <div className="panel">
        <div className="handle" />
        <h2>
          <span>{cartao ? "Novo cartão" : kind === "loan" ? "Novo crediário" : "Nova conta"}</span>
          <Key icon="x" title="Fechar" onClick={onClose} />
        </h2>
        <Field label="Nome">
          <input value={nome} autoFocus onChange={(e) => setNome(e.target.value)} placeholder="Ex.: Santander" />
        </Field>
        <Field label="Tipo">
          <select value={kind} onChange={(e) => setKind(e.target.value as AccountKind)}>
            {KINDS.map((k) => (
              <option key={k.id} value={k.id}>
                {k.label}
              </option>
            ))}
          </select>
        </Field>
        <FieldGroup label="Cor">
          <div className="chips">
            {CORES_CONTA.map((c) => (
              <button
                key={c}
                type="button"
                className="chip"
                aria-pressed={cor === c}
                aria-label={`Cor ${c}`}
                onClick={() => setCor(c)}
              >
                <i style={{ background: c }} />
              </button>
            ))}
          </div>
        </FieldGroup>
        {!passivo && (
          <Field label="Saldo hoje" className="amount">
            <input inputMode="decimal" value={saldo} onChange={(e) => setSaldo(e.target.value)} />
          </Field>
        )}
        {cartao && (
          <FieldGroup label="Fecha e vence">
            <div className="row2">
              <input
                inputMode="numeric"
                value={fecha}
                onChange={(e) => setFecha(Number(e.target.value) || 1)}
                aria-label="Dia do fechamento"
              />
              <input
                inputMode="numeric"
                value={vence}
                onChange={(e) => setVence(Number(e.target.value) || 1)}
                aria-label="Dia do vencimento"
              />
            </div>
          </FieldGroup>
        )}
        {cartao && (
          <Field label="Limite (opcional)" className="amount">
            <input inputMode="decimal" value={limite} onChange={(e) => setLimite(e.target.value)} />
          </Field>
        )}
        {passivo && gastaveis.length > 0 && (
          <>
            <FieldGroup label={cartao ? "A fatura é paga de" : "A parcela sai de"}>
              <div className="chips">
                {gastaveis.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className="chip"
                    style={ALVO}
                    aria-pressed={liquidacao === a.id}
                    onClick={() => setLiquidaEm(a.id)}
                  >
                    <i style={{ background: a.color ?? "var(--muted)" }} />
                    {a.name}
                  </button>
                ))}
              </div>
            </FieldGroup>
            {crediario && contaLiquidacao && (
              <div className="rule">
                No vencimento de cada parcela o motor tira o valor de {contaLiquidacao.name} e abate
                no crediário. É isso que faz a dívida sair do caixa.
              </div>
            )}
          </>
        )}
        {passivo && gastaveis.length === 0 && (
          <div className="banner" role="alert">
            {crediario
              ? "Sem conta de liquidação este crediário não move dinheiro nenhum: o motor tira a parcela de uma conta gastável no vencimento, e não há nenhuma. Crie a conta do banco primeiro — depois escolha aqui de onde a parcela sai."
              : "Crie primeiro a conta de onde o dinheiro sai, senão a fatura não entra na projeção."}
          </div>
        )}

        {crediario && (
          <>
            <div className="rule" style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--grid)" }}>
              <b>A dívida (opcional).</b> Preencha o que a loja combinou e o crediário já nasce com
              as parcelas na projeção. Em branco, cria só a conta — a dívida pode ser declarada
              depois em Planejado → Novo parcelamento.
            </div>
            <Field label="Credor ou loja">
              <input
                value={credor}
                placeholder="Ex.: Magazine Luiza"
                onChange={(e) => setCredor(e.target.value)}
              />
            </Field>
            <FieldGroup label="O valor que você tem é">
              <div className="chips">
                <button
                  type="button"
                  className="chip"
                  style={ALVO}
                  aria-pressed={porTotal}
                  onClick={() => setPorTotal(true)}
                >
                  O total da dívida
                </button>
                <button
                  type="button"
                  className="chip"
                  style={ALVO}
                  aria-pressed={!porTotal}
                  onClick={() => setPorTotal(false)}
                >
                  O valor da parcela
                </button>
              </div>
            </FieldGroup>
            <FieldGroup label={porTotal ? "Total e em quantas vezes" : "Valor da parcela e em quantas vezes"}>
              <div className="row2">
                <input
                  inputMode="decimal"
                  placeholder="0,00"
                  value={valorDivida}
                  aria-label={porTotal ? "Total da dívida" : "Valor da parcela"}
                  onChange={(e) => setValorDivida(e.target.value)}
                />
                <input
                  inputMode="numeric"
                  placeholder="12"
                  value={vezes}
                  aria-label="Número de parcelas"
                  onChange={(e) => setVezes(e.target.value)}
                />
              </div>
            </FieldGroup>
            {informado > 0 && parcelas >= 1 && (
              <div className="rule">
                {porTotal
                  ? `${parcelas}× de ${money(total / parcelas)} · total ${money(total)}`
                  : `total de ${money(total)} em ${parcelas}×`}
              </div>
            )}
            {informado > 0 && parcelas < 1 && (
              <div className="rule">Diga em quantas vezes, senão não dá para projetar nada.</div>
            )}
            <FieldGroup label="Primeira parcela vence e quantas já foram pagas">
              <div className="row2">
                <input
                  type="date"
                  value={primeira}
                  aria-label="Vencimento da primeira parcela"
                  onChange={(e) => setPrimeira(e.target.value)}
                />
                <input
                  inputMode="numeric"
                  value={jaPagas}
                  aria-label="Parcelas já pagas"
                  onChange={(e) => setJaPagas(e.target.value)}
                />
              </div>
            </FieldGroup>
            <div className="rule">
              Dívida que começou antes: diga quantas já foram. O motor gera só as que faltam.
            </div>
          </>
        )}

        {erro && <div className="banner" role="alert">{erro}</div>}
        <div className="acts">
          <Key onClick={onClose}>Cancelar</Key>
          <Key primary style={ALVO} disabled={!nome || ocupado} onClick={enviar}>
            {criar.isPending ? "Criando…" : criarPlano.isPending ? "Declarando a dívida…" : "Criar"}
          </Key>
        </div>
      </div>
    </div>
  );
}

/** Primeira vez: sem conta nenhuma, o painel não tem o que medir. */
export function PrimeiroUso() {
  const criar = useCreateAccount();
  const [nome, setNome] = useState("");
  const [saldo, setSaldo] = useState("");
  const [pronto, setPronto] = useState(false);

  const comecar = async () => {
    await api.seedCategories().catch(() => {});
    await criar.mutateAsync({
      name: nome.trim(),
      kind: "checking",
      initial_balance: parseMoney(saldo),
    });
    setPronto(true);
  };

  if (pronto) return null;

  return (
    <section className="view on">
      <div className="plan">
        <Face className="meter">
          <Label>Bem-vindo ao Fluxo</Label>
          <div className="reading" style={{ fontSize: 26 }}>
            Uma conta para começar
          </div>
          <div className="sub">
            O painel mede o dinheiro que entra e sai de algum lugar. Diga qual é a conta onde ele
            passa e quanto tem nela hoje; o resto entra com o tempo.
          </div>
          <Field label="Nome da conta">
            <input value={nome} autoFocus onChange={(e) => setNome(e.target.value)} placeholder="Ex.: Santander" />
          </Field>
          <Field label="Saldo hoje" className="amount">
            <input inputMode="decimal" value={saldo} onChange={(e) => setSaldo(e.target.value)} placeholder="0,00" />
          </Field>
          <div style={{ marginTop: 12 }}>
            <Key primary wide disabled={!nome.trim() || criar.isPending} onClick={comecar}>
              {criar.isPending ? "Criando…" : "Começar"}
            </Key>
          </div>
        </Face>
      </div>
    </section>
  );
}
